import { observable, action, computed, runInAction } from "mobx";
import { Subject } from "rxjs";
import { sleep } from "@citychallenge/common";
import { AxiosResponse } from "axios";

export interface LoaderError extends Error {
  response?: AxiosResponse<any>;
}

export interface LoadableOptions<T extends {} | undefined> {
  /**
   * Data retrieval function that is executed every time an update
   * is requested. Retrieval function should return a Promise
   */

  /**
   * Set a default. {Loadable.value} is initialized with this value.
   */
  defaultValue?: T;

  /**
   * Transform the value based on the previous and current value of the loadable.
   * Called whenever a new value is fetched from the server.
   */
  transformer?: (prev: T | undefined, current: T) => T;

  /**
   * Wether this loadable contains data that needs to be refreshed if the app
   * language changed
   */
  translatedContent?: boolean;

  /**
   * Duration in seconds after which the app needs to try to refresh the content
   * when a page is visited
   */
  refreshInterval?: number;

  defaultRequestOptions?: LoadableRequestOptions;
}

/**
 * Options for the fetch request inside the loadable
 */
export interface LoadableRequestOptions {
  /**
   * Delay before starting to load the data
   */
  delay?: number;
  /**
   * Reset the data before loading
   */
  reset?: boolean;
  /**
   * Wether or not to reload the data regardless of the current cache state
   */
  reloadData?: boolean;
}

/**
 * Loadable abstracts a data retrieving function and cached the result of that
 * function as a mobx observable value. The cached data can be refreshed at any
 * point in time by calling `loadable.refresh()`, which in turn refreshes
 * the underlying observable value.
 *
 * A Loadable is intended to be used in conjuction with a Loader component,
 * which manages the lifecycle - and cache duration - of the data.
 *
 * TODO: Refactor Class so that it accepts an array of Loadables which it
 * depends on. Those Loadables need to resolve before this loadable can load.
 */
export class Loadable<T extends {} | undefined> {
  private ready$ = new Subject<T>();

  /**
   * Whenever underying data is ready for the first time,
   * the `ready` promise resolves
   */
  public ready = this.ready$.toPromise();

  /**
   * Don't call directly, use Loadble.create instead
   */
  private constructor(
    private fetch: (requestOptions?: LoadableRequestOptions) => Promise<T>,
    public readonly options: LoadableOptions<T>
  ) {
    this.value = options.defaultValue;
  }

  /**
   * Create a new Loadable
   * @param fetch Data retrieval function that is executed every time an update
   * is requested. Retrieval function should return a Promise
   * @param options Options for this loadable request
   */
  public static create<T extends {} | undefined>(
    fetch: (requestOptions?: LoadableRequestOptions) => Promise<T>,
    options: LoadableOptions<T> = {}
  ) {
    return new Loadable(fetch, options);
  }

  /**
   * The mobx observable that changes whenever new data is fetched
   */
  @observable
  public value: T | undefined = undefined;

  @computed
  public get getter() {
    return this.value;
  }

  @action
  private setValue(value: T | undefined) {
    this.value = value;
  }

  @observable
  public lastRefresh?: Date | undefined;

  private updateLastRefresh() {
    this.lastRefresh = new Date();
  }

  /**
   * True when lastRefresh was longer ago than options.refreshInterval
   */
  @computed
  public get isStale() {
    const { refreshInterval } = this.options;
    if (!refreshInterval) return false;
    if (!this.lastRefresh) return true;

    const invalidWhen = +this.lastRefresh + refreshInterval * 1000;

    return +new Date() > invalidWhen;
  }

  /**
   * Whenever an error occurred, the error value is set to true
   */
  @observable
  public error?: LoaderError = undefined;

  /**
   * Set error of the loadable object. Use this if you have an error in an async
   * function you called before refreshing this loadable.
   *
   * TODO: Make private and use RequestOptions to specify the dependencies
   */
  @action
  public setError(error?: LoaderError) {
    this.error = error;
  }

  /**
   * Observable that is set to true when data is loaded for the first time
   * even if the retrieved data was falsy
   */
  @computed
  public get didReceiveValue() {
    return !!this.lastRefresh;
  }

  /**
   * Sets error and value to undefined.
   *
   * Note: the Loadable.ready still resolves to the first value retrieved
   */
  @action
  public reset() {
    this.error = undefined;
    this.value = undefined;
  }

  /**
   * Ensure that the value is load. Returns a promise with the value of this
   * loadable whenever it is retrieved for the first time
   * @param options Request options for the fetch operation.
   */
  public ensuredValue = async (
    options?: LoadableRequestOptions
  ): Promise<T> => {
    if (options?.reset || options?.reloadData) {
      return await this.refresh();
    }

    if (this.value !== undefined) return this.value;
    else {
      return await this.refresh();
    }
  };

  private _currentOperation?: Promise<T>;

  /**
   * Refresh the data. Also used to initially load the data
   */
  public async refresh(requestOptions: LoadableRequestOptions = {}) {
    const { reset, delay } = requestOptions;
    if (!this._currentOperation) {
      if (reset) this.reset();
      else this.setError(undefined);

      try {
        this._currentOperation = this._refreshInternal(requestOptions, delay);
        const response = await this._currentOperation;

        return response;
      } finally {
        this._currentOperation = undefined;
      }
    } else {
      return this._currentOperation;
    }
  }

  /**
   * Fetch and transform a value
   */
  private async _refreshInternal(
    requestOptions: LoadableRequestOptions,
    delay?: number
  ) {
    this.setError(undefined);

    try {
      if (delay) await sleep(delay);
      const result = await this.fetch(requestOptions);
      const { transformer } = this.options;
      const value = transformer ? transformer(this.value, result) : result;

      runInAction(() => {
        this.setValue(value);
        this.updateLastRefresh();
      });
      return value;
    } catch (e) {
      this.setError(e);
      throw e;
    }
  }
}
